import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import * as Yup from 'yup';
import { useFormik } from 'formik';

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
//Import Flatepicker
import 'flatpickr/dist/themes/material_blue.css';

import {
    addNewBenefit as onAddNewBenefit,
    updateBenefit as onUpdateBenefit,
} from 'store/actions';

import { BenefitId, BillingName } from '../Audits/BenefitCol';
import { useMutation, useQueryClient } from 'react-query';

//redux
import { useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
// import BenefitsModal from "./BenefitsModal";

import { Col, Row, Card, CardBody } from 'reactstrap';
import moment from 'moment';
import { ToastContainer } from 'react-toastify';
import { fetchAccessApi } from 'api/access';

function AccessAttempts() {
    const [pages, setPages] = useState(0);
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const [pagination, setPagination] = useState({
        pageSize: 10,
        pageNumber: 1,
        sortBy: 'id',
        ascendingSort: false,
        searchColumns: [],
    });

    const {
        mutate: mutateAccessAttempts,
        isLoading: isLoadingAccessAttempts,
        data: accessAttempts,
    } = useMutation(
        (payload) => {
            // Construct the base payload
            const basePayload = {
                fromDate: '2024-01-01',
                toDate: moment().format('YYYY-MM-DD'),
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                sortBy: payload.sortBy,
                ascending: payload.ascendingSort,
            };

            // // If any searchParams are provided, include only non-empty ones
            // if (searchParams.principal || searchParams.shofcoNumber || searchParams.mobile) {
            //     const cleanedSearchParams = removeEmptyFields(searchParams);
            //     return fetchAdvancedAuditsApi({
            //         ...basePayload,
            //         ...cleanedSearchParams,
            //     });
            // }
            return fetchAccessApi(payload);
        },
        {
            onSuccess: (res) => {
                res.pages > 0 && setPages(res.pages);
            },
            onSettled: () => {
                queryClient.invalidateQueries('get-all-audits');
            },
        },
    );

    useEffect(() => {
        mutateAccessAttempts({
            ...pagination,
            pageNumber: pagination.pageNumber - 1,
        });
    }, [pagination]);

    const onChangePagination = ({ page, limit }, filters) => {
        const searchColumns = isEmpty(filters)
            ? []
            : [
                ...(filters?.principal ? [{ name: 'principal', value: filters.principal }] : []),
                ...(filters?.shofcoNumber ? [{ name: 'shofcoNumber', value: filters.shofcoNumber }] : []),
                ...(filters?.mobile ? [{ name: 'mobile', value: filters.mobile }] : []),
            ];

        // setSearchParams({
        //     principal: filters?.principal || '',
        //     shofcoNumber: filters?.shofcoNumber || '',
        //     mobile: filters?.mobile || '',
        // });

        setPagination({
            ...pagination,
            pageNumber: page || pagination.pageNumber,
            pageSize: limit || pagination.pageSize,
            searchColumns,
        });
    };


    const getStatusBadge = (status) => {
        const styles = {
            SUCCESS: 'bg-green-100 text-green-800',
            FAILED: 'bg-red-100 text-red-800',
            LOCKED: 'bg-orange-100 text-orange-800',
            DISABLED: 'bg-gray-100 text-gray-800',
        };
        return <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>{status}</span>;
    };

    // const getDeviceIcon = (userAgent) => {
    //     if (userAgent?.includes('Mobile')) return <Smartphone className="w-4 h-4" />;
    //     if (userAgent?.includes('Tablet')) return <Tablet className="w-4 h-4" />;
    //     return <Monitor className="w-4 h-4" />;
    // };
    const columns = useMemo(
        () => [
            {
                Header: 'User',
                accessor: 'username',
                filterable: true,
                Cell: ({ value }) => <span>{value || '-'}</span>,
            },
            {
                Header: 'User Access Time',
                accessor: 'attemptedAt',
                filterable: true,
                Cell: ({ value }) => {
                    const formattedDate = value
                        ? moment(value).format('YYYY MMM DD h:mm A')
                        : '-';
                    return <span>{formattedDate}</span>;
                },
            },
            {
                Header: 'IP Address',
                accessor: 'ipAddress',
                filterable: true,
                Cell: ({ value }) => <span>{value || '-'}</span>,
            },
            {
                Header: 'Location',
                accessor: 'location',
                filterable: true,
                Cell: ({ value }) => {
                    const location = value ? `${value.city || ''}${value.country ? ', ' + value.country : ''} - ${value.isp}`
                        : '-';
                    return <span>{location}</span>;
                },
            },
            {
                Header: 'Device',
                accessor: 'deviceInfo',
                filterable: true,
                Cell: ({ value }) => {
                    const {deviceType, browser} = value || {};
                    return <span>{deviceType} - {browser}</span>;
                },
            },
            {
                Header: 'Channel',
                accessor: 'channel',
                filterable: true,
                Cell: ({ value }) => getStatusBadge(value),
            },
            {
                Header: 'Status',
                accessor: 'status',
                filterable: true,
                Cell: ({ value }) => getStatusBadge(value),
            },
        ],
        [],
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs title="Home" breadcrumbItem="Audit Logs" />
                    <Row>
                        <Col xs="12">
                            <Card>
                                <CardBody>
                                    <TableContainer
                                        columns={columns}
                                        data={accessAttempts?.list?.reverse() || []}
                                        isGlobalFilter={true}
                                        isAddOptions={false}
                                        isPagination={true}
                                        filterable={true}
                                        hasSearch
                                        resource="auditLogs"
                                        pages={pages}
                                        iscustomPageSizeOptions={true}
                                        addText=""
                                        loading={isLoadingAccessAttempts}
                                        tableClass="align-middle table-nowrap table-check"
                                        theadClass="table-light"
                                        pagination="pagination pagination-rounded justify-content-end mb-2"
                                        paginationData={pagination}
                                        onChangePagination={onChangePagination}
                                        searchByNames={[
                                            {
                                                value: 'principal',
                                                text: t('Principal Email'),
                                            },
                                            {
                                                value: 'shofcoNumber',
                                                text: t('SHOFCO ID'),
                                            },
                                            {
                                                value: 'mobile',
                                                text: t('Mobile Number'),
                                            },
                                        ]}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <ToastContainer />
        </React.Fragment>
    )
}

export default AccessAttempts
